import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { TcsmContentLink } from '../models/tcsm-block-models';
import { ExternalLink } from './external-link-modal';
import { VideoModal } from './video-modal';


interface Props {
    link: TcsmContentLink;
    text?: string;
}

export const TcsmContentLinkView = (props: Props) => {

    const { link, text } = props;
    const [videoOpen, setVideoOpen] = useState(false);
    const [videoEmbedUrl, setVideoEmbedUrl] = useState('');

    const linkText = text || link?.linkText;
    const linkTextNode = (
        <>
            {linkText} {link?.addAsterisk && <span className='asterisk'>*</span>}
            {
                link?.isPdf &&
                <PictureAsPdfIcon />
            }
        </>

    );

    const normalizeExternalUrl = (url: string) => {
        if (!url) return url;
        try {
            const u = new URL(url);
            // Handle YouTube embed URL -> watch URL
            if (u.host.includes('youtube.com') && u.pathname.startsWith('/embed/')) {
                const videoId = u.pathname.replace('/embed/', '');
                return `https://www.youtube.com/watch?v=${videoId}`;
            }
        } catch (_e) {
            // If URL parsing fails, return original
        }
        return url;
    };

    const isVideoUrl = (url: string) => {
        if (!url) return false;
        try {
            const u = new URL(url);
            const host = u.host.toLowerCase();
            return host.includes('youtube.com') || host.includes('youtu.be') || host.includes('vimeo.com');
        } catch {
            return false;
        }
    };

    const toEmbedUrl = (url: string) => {
        if (!url) return url;
        try {
            const u = new URL(url);
            const host = u.host.toLowerCase();
            // YouTube conversions
            if (host.includes('youtu.be')) {
                const id = u.pathname.replace('/', '');
                return `https://www.youtube.com/embed/${id}`;
            }
            if (host.includes('youtube.com')) {
                if (u.pathname.startsWith('/embed/')) return `${u.origin}${u.pathname}${u.search || ''}`;
                const id = u.searchParams.get('v');
                if (id) return `https://www.youtube.com/embed/${id}`;
            }
            // Vimeo conversions
            if (host.includes('vimeo.com')) {
                // Handles player.vimeo.com/video/{id} and vimeo.com/{id}
                const parts = u.pathname.split('/').filter(Boolean);
                const id = parts[0] === 'video' && parts.length > 1 ? parts[1] : parts[0];
                if (id) return `https://player.vimeo.com/video/${id}`;
            }
        } catch {
            return url;
        }
        return url;
    };

    function RenderLink(){

        if (!link) return null;
        

        if (!link.reactLink) {
            const url = link?.linkUrl;
            if (isVideoUrl(url)) {
                const embed = toEmbedUrl(url);
                const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    setVideoEmbedUrl(embed);
                    setVideoOpen(true);
                };
                return (
                    <>
                        <a href={url} onClick={handleClick}>{linkTextNode}</a>
                        <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} embedUrl={videoEmbedUrl} />
                    </>
                );
            }
            const externalUrl = normalizeExternalUrl(url);
            return <ExternalLink linkUrl={externalUrl} linkText={linkTextNode} newWindow={link?.newWindow} />;
        }

        // For internal/react links, render a react-router Link and respect newWindow when specified.
        return <Link to={link?.linkUrl} target={link?.newWindow ? '_blank' : undefined} rel={link?.newWindow ? 'noopener noreferrer' : undefined}>{linkTextNode}</Link>;
    }

    return (
        <>
        <RenderLink/>            
        </>
    );

};

TcsmContentLinkView.defaultProps = {
    text: '',
};


